import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Badge from "@material-ui/core/Badge";
import { makeStyles } from "@material-ui/core/styles";
import NotificationsIcon from "@material-ui/icons/Notifications";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  BellNotificationItem,
  fetchBellNotifications,
  getFirebaseWebConfigFromEnv,
  isFcmBrowserSupported,
  registerFcmTokenWithBackend,
  requestFcmToken,
  subscribeForegroundMessages,
} from "../../utils/firebaseMessaging";

const NOTIFICATION_STORAGE_KEY = "bell_notifications_v1";
const NOTIFICATION_UNREAD_STORAGE_KEY = "bell_notifications_unread_v1";
const NOTIFICATION_SEEN_KEYS_STORAGE_KEY = "bell_notifications_seen_keys_v1";
const MAX_NOTIFICATIONS = 20;

const useStyles = makeStyles((theme) => ({
  panel: {
    width: 360,
    maxHeight: 460,
    overflow: "auto",
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 0.5, 1),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
    lineHeight: 1.5,
    padding: theme.spacing(1),
  },
  list: {
    padding: 0,
    marginTop: theme.spacing(2),
  },
  listItem: {
    alignItems: "flex-start",
    borderRadius: 8,
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(1, 1.25),
    backgroundColor: theme.palette.action.hover,
  },
  primaryText: {
    fontSize: "0.88rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  secondaryText: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  iconButton: {
    padding: 8,
    margin: 0,
  },
  badge: {
    "& .MuiBadge-badge": {
      minWidth: 18,
      height: 18,
      fontSize: "0.68rem",
      fontWeight: 700,
      borderRadius: 9,
    },
  },
}));

const NotificationBell: FC = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [messages, setMessages] = useState<BellNotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [fcmSupported, setFcmSupported] = useState<boolean>(true);
  const [browserUnsupportedMessage, setBrowserUnsupportedMessage] = useState<
    string | null
  >(null);

  const configOk = useMemo(() => !!getFirebaseWebConfigFromEnv(), []);

  const mergeNotifications = useCallback(
    (
      current: BellNotificationItem[],
      incoming: BellNotificationItem[],
    ): BellNotificationItem[] => {
      const byKey = new Map<string, BellNotificationItem>();
      [...incoming, ...current].forEach((item) => {
        byKey.set(item.key, item);
      });
      return Array.from(byKey.values())
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
        .slice(0, MAX_NOTIFICATIONS);
    },
    [],
  );

  useEffect(() => {
    try {
      const rawItems = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
      if (rawItems) {
        const parsed = JSON.parse(rawItems) as BellNotificationItem[];
        if (Array.isArray(parsed)) {
          setMessages(parsed.slice(0, MAX_NOTIFICATIONS));
        }
      }
      const rawUnread = localStorage.getItem(NOTIFICATION_UNREAD_STORAGE_KEY);
      if (rawUnread) {
        const parsedUnread = Number(rawUnread);
        if (!Number.isNaN(parsedUnread) && parsedUnread >= 0) {
          setUnreadCount(parsedUnread);
        }
      }
    } catch (_error) {
      // ignore parse/storage errors
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(messages));
    } catch (_error) {
      // ignore storage write errors
    }
  }, [messages]);

  useEffect(() => {
    try {
      localStorage.setItem(
        NOTIFICATION_UNREAD_STORAGE_KEY,
        String(unreadCount),
      );
    } catch (_error) {
      // ignore storage write errors
    }
  }, [unreadCount]);

  const refetchNotifications = useCallback(async () => {
    try {
      const fetched = await fetchBellNotifications(MAX_NOTIFICATIONS);
      if (fetched.length === 0) {
        return;
      }
      const rawSeen = localStorage.getItem(NOTIFICATION_SEEN_KEYS_STORAGE_KEY);
      const seenKeys = new Set<string>(
        rawSeen ? (JSON.parse(rawSeen) as string[]) : [],
      );
      const unseenNewKeys = fetched
        .map((item) => item.key)
        .filter((key) => !seenKeys.has(key));
      if (unseenNewKeys.length > 0) {
        setUnreadCount((prev) => prev + unseenNewKeys.length);
      }
      const latestSeenKeys = Array.from(
        new Set(fetched.map((item) => item.key)),
      );
      localStorage.setItem(
        NOTIFICATION_SEEN_KEYS_STORAGE_KEY,
        JSON.stringify(latestSeenKeys),
      );
      setMessages((prev) => mergeNotifications(prev, fetched));
    } catch (_error) {
      // keep silent, bell falls back to cached notifications
    }
  }, [mergeNotifications]);

  useEffect(() => {
    void (async () => {
      const supported = await isFcmBrowserSupported();
      setFcmSupported(supported);
    })();
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const nav = navigator as Navigator & { brave?: unknown };
    const isBrave = typeof nav.brave !== "undefined" || ua.includes("brave");
    if (isBrave) {
      setBrowserUnsupportedMessage(
        "Push notifications are not supported in Brave yet.",
      );
      return;
    }
    setBrowserUnsupportedMessage(null);
  }, []);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;

    void (async () => {
      const unsub = await subscribeForegroundMessages((_payload) => {
        if (!active) return;
        // Keep backend as source of truth so list length matches API data.
        void refetchNotifications();
      });
      if (active) {
        unsubscribe = unsub;
      }
    })();

    return () => {
      active = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [refetchNotifications]);

  const open = Boolean(anchorEl);

  const registerFcmIfPermitted = useCallback(async () => {
    if (!configOk) return;
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    try {
      const nextToken = await requestFcmToken();
      if (!nextToken) return;
      await registerFcmTokenWithBackend({ token: nextToken });
    } catch (_error) {
      // keep silent: this menu only shows user notifications, not technical state
    }
  }, [configOk]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setUnreadCount(0);
    // Several browsers require a user gesture for the permission prompt.
    void (async () => {
      if (!configOk) return;
      if (!("Notification" in window)) return;
      if (Notification.permission === "default") {
        await Notification.requestPermission();
      }
    })();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // const handleClearNotifications = () => {
  //   setMessages([]);
  //   setUnreadCount(0);
  //   try {
  //     localStorage.removeItem(NOTIFICATION_STORAGE_KEY);
  //     localStorage.removeItem(NOTIFICATION_UNREAD_STORAGE_KEY);
  //     localStorage.removeItem(NOTIFICATION_SEEN_KEYS_STORAGE_KEY);
  //   } catch (_error) {
  //     // ignore storage write errors
  //   }
  // };

  useEffect(() => {
    const registerInBackground = async () => {
      await registerFcmIfPermitted();
    };

    void registerInBackground();
  }, [registerFcmIfPermitted]);

  useEffect(() => {
    void refetchNotifications();
  }, [refetchNotifications]);

  useEffect(() => {
    const handleFocus = () => {
      void refetchNotifications();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void refetchNotifications();
      }
    };
    const handleOnline = () => {
      void refetchNotifications();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
    };
  }, [refetchNotifications]);

  const panelTitle = "Push notifications";

  const unsupportedBannerText = useMemo(() => {
    if (browserUnsupportedMessage) {
      return browserUnsupportedMessage;
    }
    if (fcmSupported) {
      return null;
    }
    const ua = navigator.userAgent;
    const isIos =
      /iPhone|iPad|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone ===
        true;
    if (isIos && !standalone) {
      return "To receive push notifications on iPhone or iPad, add this app to your Home Screen (Share → Add to Home Screen), then open it from the home screen icon.";
    }
    return "This browser does not support web push notifications.";
  }, [browserUnsupportedMessage, fcmSupported]);

  const getDownloadData = (message: BellNotificationItem) => {
    const raw = message.payload;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as {
        download_url?: string;
        file_name?: string;
      };
      if (!parsed.download_url) return null;
      return parsed;
    } catch (_error) {
      return null;
    }
  };

  return (
    <>
      <Tooltip title={panelTitle}>
        <IconButton
          color="inherit"
          aria-label={panelTitle}
          aria-owns={open ? "notification-bell-menu" : undefined}
          aria-haspopup="true"
          onClick={handleOpen}
          edge="end"
          className={classes.iconButton}
        >
          <Badge
            color="secondary"
            badgeContent={unreadCount}
            max={99}
            className={classes.badge}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id="notification-bell-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        getContentAnchorEl={null}
      >
        <Box className={classes.panel}>
          <Box className={classes.header}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Notifications
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography variant="caption" className={classes.muted}>
                {messages.length} recent
              </Typography>
              {/* {messages.length > 0 && (
                <Button
                  size="small"
                  color="primary"
                  onClick={handleClearNotifications}
                  style={{ marginLeft: 8, minWidth: 0, padding: "2px 6px" }}
                >
                  Clear
                </Button>
              )} */}
            </Box>
          </Box>
          <Divider />
          {unsupportedBannerText && (
            <Typography className={classes.muted} variant="body2">
              {unsupportedBannerText}
            </Typography>
          )}
          {messages.length === 0 ? (
            <Typography className={classes.muted} variant="body2">
              No notifications yet.
            </Typography>
          ) : (
            <List dense disablePadding className={classes.list}>
              {messages.map((m, i) => (
                <ListItem key={i} className={classes.listItem}>
                  <Box width="100%">
                    <ListItemText
                      primaryTypographyProps={{
                        className: classes.primaryText,
                      }}
                      secondaryTypographyProps={{
                        className: classes.secondaryText,
                      }}
                      primary={m.title}
                      secondary={m.body}
                    />
                    {getDownloadData(m)?.download_url && (
                      <Box mt={1}>
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => {
                            const download = getDownloadData(m);
                            if (!download?.download_url) return;
                            const link = document.createElement("a");
                            link.href = download.download_url;
                            if (download.file_name) {
                              link.setAttribute("download", download.file_name);
                            }
                            link.setAttribute("target", "_blank");
                            link.setAttribute("rel", "noopener noreferrer");
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          Download CSV
                        </Button>
                      </Box>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
